import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

import { haversineMeters } from "../../shared/geo"
import { USABLE_OPERATIONAL_STATUSES } from "../../shared/vehicle"
import {
  type AssignmentShift,
  bufferWindow,
  checkVehicleAssignment,
} from "../../shared/vehicle-assignment"

export interface CandidateVehicle {
  id: string
  registrationDisplay: string
  vehicleGroup: string | null
  /** Metres from the block's depot, null when either side has no coordinates. */
  depotDistanceMeters: number | null
  /** Metres from the driver's sticky location, null when it or its outcode has no coordinates. */
  stickyDistanceMeters: number | null
}

export interface CandidateShift extends AssignmentShift {
  id: string
  vehicleId: string | null
  /** The holder's sticky location code, used to measure how far each vehicle is from their area. */
  stickyLocation?: string | null
}

// Vehicles that pass every criterion for one block, nearest the driver's sticky location first,
// then nearest the depot. Same check the server re-runs on submit, so the list matches what will be accepted.
export function useCandidateVehicles(shift: CandidateShift | undefined) {
  return useQuery({
    queryKey: ["candidate-vehicles", shift?.id, shift?.stickyLocation ?? null],
    enabled: Boolean(shift),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CandidateVehicle[]> => {
      const target = shift!

      // sticky_locations already holds the outcode centroid, so the driver's area is one lookup by
      // code rather than a geocode.
      const stickyPromise = target.stickyLocation
        ? supabase
            .from("sticky_locations")
            .select("latitude, longitude")
            .eq("code", target.stickyLocation)
            .maybeSingle()
        : null

      const window = bufferWindow(target)

      // Depot and conflicting blocks both ride along on their FKs. The filters on the embedded
      // shifts narrow that list per vehicle; they do not drop vehicles with no conflicts.
      const { data: vehicles, error } = await supabase
        .from("vehicles")
        .select(
          "id, registration_display, operational_status, depot_id, vehicle_group, latitude, longitude, depots(latitude, longitude), shifts!shifts_vehicle_id_fkey(id, start_time, end_time, status)",
        )
        .eq("depot_id", target.depot_id ?? "")
        .in("operational_status", USABLE_OPERATIONAL_STATUSES)
        .neq("shifts.status", "Cancelled")
        .neq("shifts.id", target.id)
        .lt("shifts.start_time", window.to)
        .gt("shifts.end_time", window.from)

      if (error) throw error

      const rows = vehicles ?? []
      if (rows.length === 0) return []

      // A missing or uncoded sticky location just leaves that distance unknown; it never hides a
      // vehicle, since eligibility does not depend on it. Its coordinates are text columns, so a
      // non-numeric value has to read as unknown rather than NaN.
      const stickyRow = stickyPromise ? ((await stickyPromise).data ?? null) : null
      const stickyLat = Number(stickyRow?.latitude)
      const stickyLon = Number(stickyRow?.longitude)
      const sticky =
        stickyRow?.latitude != null &&
        stickyRow?.longitude != null &&
        Number.isFinite(stickyLat) &&
        Number.isFinite(stickyLon)
          ? { latitude: stickyLat, longitude: stickyLon }
          : null

      const candidates = rows.flatMap((row) => {
        const check = checkVehicleAssignment(row, target, row.shifts ?? [])
        if (!check.ok) return []

        const depot = row.depots
        const hasPosition = row.latitude != null && row.longitude != null
        const from = { latitude: row.latitude, longitude: row.longitude }

        return [
          {
            id: row.id,
            registrationDisplay: row.registration_display,
            vehicleGroup: row.vehicle_group,
            depotDistanceMeters:
              hasPosition && depot?.latitude != null && depot?.longitude != null
                ? haversineMeters(from, { latitude: depot.latitude, longitude: depot.longitude })
                : null,
            stickyDistanceMeters: hasPosition && sticky ? haversineMeters(from, sticky) : null,
          },
        ]
      })

      // Nearest the driver's area first, then nearest the depot. Unknown distances sink within each
      // key - a van with no recorded position is not a better suggestion than one parked nearby.
      // With no sticky location every vehicle ties on the first key, leaving depot order intact.
      const byDistance = (a: number | null, b: number | null) => {
        if (a === null) return b === null ? 0 : 1
        if (b === null) return -1
        return a - b
      }

      return candidates.sort(
        (a, b) =>
          byDistance(a.stickyDistanceMeters, b.stickyDistanceMeters) ||
          byDistance(a.depotDistanceMeters, b.depotDistanceMeters),
      )
    },
  })
}
